import themeSettingsConfig from "../config/themeSettings.json";
import {
  layoutTypes,
  leftSidebarTypes,
  layoutModeTypes,
  layoutWidthTypes,
  layoutPositionTypes,
  topbarThemeTypes,
  leftsidbarSizeTypes,
  leftSidebarViewTypes,
  leftSidebarImageTypes,
  preloaderTypes,
  sidebarVisibilitytypes
} from "../Components/constants/layout";

/**
 * Get default theme settings
 * Currently loads from JSON config file (src/config/themeSettings.json)
 * 
 * MIGRATION TO DATABASE:
 * To move to database-based user settings in the future:
 * 1. Create API endpoint: GET /api/users/:userId/theme-settings
 * 2. Update getUserThemeSettings() to call the API
 * 3. Update getDefaultThemeSettings() to accept userId and call getUserThemeSettings()
 * 4. Update reducer initialization to load user settings on app start
 * 
 * Example database schema:
 * {
 *   userId: string,
 *   layoutType: "vertical" | "horizontal" | "twocolumn" | "semibox",
 *   layoutModeType: "light" | "dark",
 *   layoutWidthType: "lg" | "boxed",
 *   layoutPositionType: "fixed" | "scrollable",
 *   leftSidebarType: "light" | "dark" | "gradient" | ...,
 *   topbarThemeType: "light" | "dark",
 *   leftsidbarSizeType: "lg" | "md" | "sm" | "sm-hover",
 *   leftSidebarViewType: "default" | "detached",
 *   leftSidebarImageType: "none" | "img-1" | "img-2" | ...,
 *   preloader: "enable" | "disable",
 *   sidebarVisibilitytype: "show" | "hidden"
 * }
 * 
 * @param {string} userId - Optional user ID for future per-user customization
 * @returns {Object} Theme settings object
 */
export const getDefaultThemeSettings = (userId = null) => {
  // For now, return default settings from JSON
  // TODO: In the future, replace this with:
  // if (userId) {
  //   return await getUserThemeSettings(userId);
  // }
  const defaultSettings = themeSettingsConfig.default;

  // Map JSON string values to constant values
  // The JSON contains the actual string values that match the constant values
  return {
    layoutType: defaultSettings.layoutType || layoutTypes.VERTICAL,
    leftSidebarType: defaultSettings.leftSidebarType || leftSidebarTypes.LIGHT,
    layoutModeType: defaultSettings.layoutModeType || layoutModeTypes.LIGHTMODE,
    layoutWidthType: defaultSettings.layoutWidthType || layoutWidthTypes.FLUID,
    layoutPositionType: defaultSettings.layoutPositionType || layoutPositionTypes.FIXED,
    topbarThemeType: defaultSettings.topbarThemeType || topbarThemeTypes.LIGHT,
    leftsidbarSizeType: defaultSettings.leftsidbarSizeType || leftsidbarSizeTypes.DEFAULT,
    leftSidebarViewType: defaultSettings.leftSidebarViewType || leftSidebarViewTypes.DEFAULT,
    leftSidebarImageType: defaultSettings.leftSidebarImageType || leftSidebarImageTypes.NONE,
    preloader: defaultSettings.preloader || preloaderTypes.DISABLE,
    sidebarVisibilitytype: defaultSettings.sidebarVisibilitytype || sidebarVisibilitytypes.SHOW
  };
};

/**
 * Get theme settings for a specific user
 * This function can be extended to fetch from database
 * 
 * MIGRATION STEPS:
 * 1. Import APIClient: import { APIClient } from "./api_helper";
 * 2. Replace the TODO section with actual API call
 * 3. Handle errors and fallback to default settings
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Theme settings object
 */
export const getUserThemeSettings = async (userId) => {
  // TODO: Replace with actual database call
  // Example implementation:
  // try {
  //   const apiClient = new APIClient();
  //   const response = await apiClient.get(`/api/users/${userId}/theme-settings`);
  //   return response.data;
  // } catch (error) {
  //   console.error("Failed to fetch user theme settings:", error);
  //   return getDefaultThemeSettings(); // Fallback to defaults
  // }
  
  // For now, return default settings
  return getDefaultThemeSettings();
};
